import { CONFIG } from "../util/config";
import { useQuery } from "@tanstack/react-query";
import { useClient } from "../context/auth-context";
import { parseApiResponse } from "../models";
import { fetchServiceJson } from "./service-fetch";

export async function postComment(payload, sectionId, sectionName, token) {
  const response = await fetchServiceJson(
    `conversation/${sectionName}/${sectionId}/comment`,
    token,
    {
      method: "POST",
      data: payload,
      returnErrorData: true,
    },
  );
  return parseApiResponse(response);
}

export const deleteComment = async (trackId, token) => {
  const response = await fetchServiceJson(
    `conversation/${trackId}/remove`,
    token,
    {
      method: "DELETE",
    },
  );

  if (!response) {
    throw new Error("Failed to delete comment");
  }

  return parseApiResponse(response);
};

export const updateComment = async (trackId, payload, token) => {
  const response = await fetchServiceJson(
    `conversation/${trackId}/edit`,
    token,
    {
      method: "PUT",
      data: payload,
    },
  );
  if (!response) {
    throw new Error("Failed to update comment");
  }
  return parseApiResponse(response);
};

export const readReachOutBoxComments = async (sectionId, token) => {
  const response = await fetchServiceJson(
    `conversation/reachout/${sectionId}/read`,
    token,
    {
      method: "PUT",
    },
  );
  if (!response) {
    throw new Error("Comments were not Read");
  }
  return parseApiResponse(response);
};

export const sendTypingEvent = async (
  action,
  user,
  sectionId,
  sectionName,
  token,
) => {
  try {
    const res = await fetch(`${CONFIG.socketServerUrl}/pushUpdate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        eventKey: `${sectionId}:${sectionName}`,
        eventData: {
          action,
          data: {
            firstName: `${user?.firstName}`,
            lastName: `${user?.lastName}`,
            id: `${user?.userId}`,
          },
        },
      }),
    });
    if (!res.ok) {
      throw new Error("Failed to update comment");
    }
  } catch (err) {
    console.error("Typing event error:", err);
  }
};

export function useReachOutBoxInfo({
  orgId,
  chatUserId,
  chatGuestMode,
  ...config
}) {
  const client = useClient();

  return useQuery(
    ["box", orgId, chatUserId, chatGuestMode],
    async () => {
      const endpoint =
        chatGuestMode || !chatUserId
          ? `conversation/reachout/organisation/${orgId}/guest/${chatUserId}/info`
          : `conversation/reachout/organisation/${orgId}/user/${chatUserId}/info`;

      return client(endpoint).then((response) => response.data);
    },
    {
      enabled: !!orgId && (chatGuestMode || !!chatUserId),
      keepPreviousData: true,
      ...config,
    },
  );
}

export function useConversationComments({
  sectionId,
  sectionName,
  page,
  limit,
  enabled,
  ...config
}) {
  const client = useClient();

  return useQuery(
    ["boxComments", sectionId, sectionName, page],
    async () => {
      const queryParams = [];
      queryParams.push(`limit=${limit}`);
      queryParams.push(`page=${page}`);
      const queryString = queryParams.length ? `?${queryParams.join("&")}` : "";

      return client(
        `conversation/${sectionName}/${sectionId}/comments${queryString}`,
      ).then((response) => parseApiResponse(response).data);
    },
    { enabled, keepPreviousData: true, ...config },
  );
}
